import * as ContextMenu from '@radix-ui/react-context-menu';
import { forwardRef, type ComponentRef, type ReactNode } from 'react';

import {
  useMonkeysBaseAppearance,
  useMonkeysComponentAttributes,
  useMonkeysDirection,
} from '../../provider/context';
import { useBaseOverlayPortal } from './internal/overlayPortal';

import type {
  BaseContextMenuCheckboxItemProps,
  BaseContextMenuContentProps,
  BaseContextMenuItemProps,
  BaseContextMenuLabelProps,
  BaseContextMenuProps,
  BaseContextMenuRadioGroupProps,
  BaseContextMenuRadioItemProps,
  BaseContextMenuSeparatorProps,
  BaseContextMenuSubContentProps,
  BaseContextMenuSubProps,
  BaseContextMenuSubTriggerProps,
  BaseContextMenuTriggerProps,
} from './types';
import {
  getBaseMenuItemToneClassName,
  resolveBaseAppearance,
} from './theme';
import { cn, hasRenderableNode } from './utils';

const contextMenuTones = [
  'default',
  'primary',
  'success',
  'warning',
  'danger',
  'muted',
] as const;

function validateBoolean(value: unknown, name: string) {
  if (typeof value !== 'boolean') {
    throw new TypeError(`BaseContextMenu ${name} must be a boolean.`);
  }
}

function validateTone(value: unknown) {
  if (
    value !== undefined &&
    !contextMenuTones.includes(
      value as (typeof contextMenuTones)[number],
    )
  ) {
    throw new TypeError(
      'BaseContextMenu tone must be "default", "primary", "success", "warning", "danger", or "muted".',
    );
  }
}

function validateDirection(value: unknown) {
  if (value !== undefined && value !== 'ltr' && value !== 'rtl') {
    throw new TypeError('BaseContextMenu dir must be "ltr" or "rtl".');
  }
}

function CheckIcon() {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 16 16"
      className="h-3.5 w-3.5"
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
    >
      <path d="M3.5 8.5 6.5 11.5 12.5 4.5" />
    </svg>
  );
}

function DotIcon() {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 16 16"
      className="h-3.5 w-3.5"
      fill="currentColor"
    >
      <circle cx="8" cy="8" r="3" />
    </svg>
  );
}

function ChevronRightIcon() {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 16 16"
      className="h-3.5 w-3.5"
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
    >
      <path d="m6 4 4 4-4 4" />
    </svg>
  );
}

function renderItemContent({
  label,
  icon,
  shortcut,
  children,
  classNames,
  inset,
  indicator,
}: Pick<
  BaseContextMenuItemProps,
  'children' | 'classNames' | 'icon' | 'inset' | 'label' | 'shortcut'
> & {
  indicator?: ReactNode;
}) {
  const resolvedLabel = children ?? label;

  return (
    <>
      {indicator ? indicator : null}
      {hasRenderableNode(icon) ? (
        <span data-slot="icon" className={cn(classNames?.icon)}>
          {icon}
        </span>
      ) : inset ? (
        <span
          aria-hidden="true"
          data-slot="icon"
          className="h-4 w-4 shrink-0"
        />
      ) : null}
      {hasRenderableNode(resolvedLabel) ? (
        <span data-slot="label" className={cn(classNames?.label)}>
          {resolvedLabel}
        </span>
      ) : null}
      {hasRenderableNode(shortcut) ? (
        <span data-slot="shortcut" className={cn(classNames?.shortcut)}>
          {shortcut}
        </span>
      ) : null}
    </>
  );
}

export function BaseContextMenu({
  children,
  trigger,
  content,
  disabled = false,
  modal = true,
  dir,
  appearance,
  className,
  classNames,
  onOpenChange,
}: BaseContextMenuProps) {
  validateBoolean(disabled, 'disabled');
  validateBoolean(modal, 'modal');
  validateDirection(dir);
  const inheritedDirection = useMonkeysDirection();
  const resolvedAppearance = useMonkeysBaseAppearance(appearance);

  return (
    <ContextMenu.Root
      onOpenChange={onOpenChange}
      modal={modal}
      dir={dir ?? inheritedDirection}
    >
      {trigger ? (
        <BaseContextMenuTrigger
          disabled={disabled}
          appearance={resolvedAppearance}
          className={cn(classNames?.trigger, className)}
        >
          {trigger}
        </BaseContextMenuTrigger>
      ) : null}
      {content ? (
        <BaseContextMenuContent
          appearance={resolvedAppearance}
          className={classNames?.content}
        >
          {content}
        </BaseContextMenuContent>
      ) : null}
      {children}
    </ContextMenu.Root>
  );
}

export const BaseContextMenuTrigger = forwardRef<
  ComponentRef<typeof ContextMenu.Trigger>,
  BaseContextMenuTriggerProps
>(function BaseContextMenuTrigger(
  {
    children,
    disabled = false,
    asChild = false,
    appearance,
    className,
    style,
    ...triggerProps
  },
  ref,
) {
  validateBoolean(disabled, 'Trigger disabled');
  validateBoolean(asChild, 'Trigger asChild');
  const resolvedAppearance = useMonkeysBaseAppearance(appearance);
  const componentAttributes = useMonkeysComponentAttributes();
  const theme = resolveBaseAppearance(resolvedAppearance);

  return (
    <ContextMenu.Trigger
      {...componentAttributes}
      {...triggerProps}
      ref={ref}
      asChild={asChild}
      disabled={disabled}
      data-monkeys-component="context-menu"
      data-slot="trigger"
      data-disabled={disabled || undefined}
      className={cn(
        !asChild && theme.slots.menuTrigger,
        disabled && !asChild && theme.slots.menuItemDisabled,
        className,
      )}
      style={style}
    >
      {children}
    </ContextMenu.Trigger>
  );
});

export const BaseContextMenuContent = forwardRef<
  ComponentRef<typeof ContextMenu.Content>,
  BaseContextMenuContentProps
>(function BaseContextMenuContent(
  {
    children,
    appearance,
    className,
    classNames,
    style,
    ...contentProps
  },
  ref,
) {
  const resolvedAppearance = useMonkeysBaseAppearance(appearance);
  const componentAttributes = useMonkeysComponentAttributes();
  const theme = resolveBaseAppearance(resolvedAppearance);
  const portal = useBaseOverlayPortal();
  const content = (
    <ContextMenu.Content
      {...componentAttributes}
      {...contentProps}
      ref={ref}
      data-monkeys-component="context-menu"
      data-slot="content"
      className={cn(theme.slots.menuContent, classNames?.root, className)}
      style={{
        ...theme.styles.menuContent,
        ...style,
      }}
    >
      {children}
    </ContextMenu.Content>
  );

  return portal.shouldPortal ? (
    <ContextMenu.Portal {...portal.radixPortalProps}>
      {content}
    </ContextMenu.Portal>
  ) : content;
});

export const BaseContextMenuLabel = forwardRef<
  ComponentRef<typeof ContextMenu.Label>,
  BaseContextMenuLabelProps
>(function BaseContextMenuLabel(
  { children, appearance, className, style, ...labelProps },
  ref,
) {
  const resolvedAppearance = useMonkeysBaseAppearance(appearance);
  const componentAttributes = useMonkeysComponentAttributes();
  const theme = resolveBaseAppearance(resolvedAppearance);

  return (
    <ContextMenu.Label
      {...componentAttributes}
      {...labelProps}
      ref={ref}
      data-monkeys-component="context-menu"
      data-slot="label"
      className={cn(theme.slots.menuLabel, className)}
      style={style}
    >
      {children}
    </ContextMenu.Label>
  );
});

export const BaseContextMenuSeparator = forwardRef<
  ComponentRef<typeof ContextMenu.Separator>,
  BaseContextMenuSeparatorProps
>(function BaseContextMenuSeparator(
  { appearance, className, style, ...separatorProps },
  ref,
) {
  const resolvedAppearance = useMonkeysBaseAppearance(appearance);
  const componentAttributes = useMonkeysComponentAttributes();
  const theme = resolveBaseAppearance(resolvedAppearance);

  return (
    <ContextMenu.Separator
      {...componentAttributes}
      {...separatorProps}
      ref={ref}
      data-monkeys-component="context-menu"
      data-slot="separator"
      className={cn(theme.slots.menuSeparator, className)}
      style={style}
    />
  );
});

export const BaseContextMenuItem = forwardRef<
  ComponentRef<typeof ContextMenu.Item>,
  BaseContextMenuItemProps
>(function BaseContextMenuItem(
  {
    children,
    label,
    icon,
    shortcut,
    tone,
    disabled = false,
    inset = false,
    appearance,
    className,
    classNames,
    style,
    onSelect,
    ...itemProps
  },
  ref,
) {
  validateBoolean(disabled, 'Item disabled');
  validateBoolean(inset, 'Item inset');
  validateTone(tone);
  const resolvedTone = tone ?? 'default';
  const resolvedAppearance = useMonkeysBaseAppearance(appearance);
  const componentAttributes = useMonkeysComponentAttributes();
  const theme = resolveBaseAppearance(resolvedAppearance);

  return (
    <ContextMenu.Item
      {...componentAttributes}
      {...itemProps}
      ref={ref}
      disabled={disabled}
      data-monkeys-component="context-menu"
      data-slot="item"
      data-tone={resolvedTone}
      data-disabled={disabled || undefined}
      className={cn(
        theme.slots.menuItem,
        getBaseMenuItemToneClassName(theme, tone),
        disabled && theme.slots.menuItemDisabled,
        classNames?.root,
        className,
      )}
      style={{
        ...theme.styles.menuItem,
        ...style,
      }}
      onSelect={(event) => {
        if (disabled) {
          event.preventDefault();
          return;
        }
        onSelect?.(event);
      }}
    >
      {renderItemContent({
        children,
        label,
        icon,
        shortcut,
        inset,
        classNames: {
          icon: cn(theme.slots.menuItemIcon, classNames?.icon),
          label: cn(theme.slots.menuItemLabel, classNames?.label),
          shortcut: cn(theme.slots.menuItemShortcut, classNames?.shortcut),
        },
      })}
    </ContextMenu.Item>
  );
});

export const BaseContextMenuCheckboxItem = forwardRef<
  ComponentRef<typeof ContextMenu.CheckboxItem>,
  BaseContextMenuCheckboxItemProps
>(function BaseContextMenuCheckboxItem(
  {
    children,
    label,
    icon,
    shortcut,
    tone,
    disabled = false,
    inset = false,
    checked,
    onCheckedChange,
    appearance,
    className,
    classNames,
    style,
    onSelect,
    ...itemProps
  },
  ref,
) {
  validateBoolean(disabled, 'CheckboxItem disabled');
  validateBoolean(inset, 'CheckboxItem inset');
  validateTone(tone);
  if (
    checked !== undefined &&
    checked !== 'indeterminate' &&
    typeof checked !== 'boolean'
  ) {
    throw new TypeError(
      'BaseContextMenu CheckboxItem checked must be a boolean or "indeterminate".',
    );
  }
  const resolvedTone = tone ?? 'default';
  const resolvedAppearance = useMonkeysBaseAppearance(appearance);
  const componentAttributes = useMonkeysComponentAttributes();
  const theme = resolveBaseAppearance(resolvedAppearance);

  return (
    <ContextMenu.CheckboxItem
      {...componentAttributes}
      {...itemProps}
      ref={ref}
      checked={checked}
      disabled={disabled}
      data-monkeys-component="context-menu"
      data-slot="checkbox-item"
      data-tone={resolvedTone}
      data-disabled={disabled || undefined}
      data-checked={
        checked === 'indeterminate'
          ? 'mixed'
          : checked === undefined
            ? undefined
            : String(checked)
      }
      onCheckedChange={(nextChecked) => {
        if (disabled) return;
        onCheckedChange?.(nextChecked);
      }}
      className={cn(
        theme.slots.menuItem,
        'pl-8',
        getBaseMenuItemToneClassName(theme, tone),
        disabled && theme.slots.menuItemDisabled,
        classNames?.root,
        className,
      )}
      style={{
        ...theme.styles.menuItem,
        ...style,
      }}
      onSelect={(event) => {
        if (disabled) {
          event.preventDefault();
          return;
        }
        onSelect?.(event);
      }}
    >
      {renderItemContent({
        children,
        label,
        icon,
        shortcut,
        inset,
        classNames: {
          icon: cn(theme.slots.menuItemIcon, classNames?.icon),
          label: cn(theme.slots.menuItemLabel, classNames?.label),
          shortcut: cn(theme.slots.menuItemShortcut, classNames?.shortcut),
        },
        indicator: (
          <ContextMenu.ItemIndicator
            data-slot="indicator"
            className={cn(theme.slots.menuCheckboxIndicator, classNames?.indicator)}
          >
            <CheckIcon />
          </ContextMenu.ItemIndicator>
        ),
      })}
    </ContextMenu.CheckboxItem>
  );
});

export function BaseContextMenuRadioGroup({
  children,
  value,
  onValueChange,
}: BaseContextMenuRadioGroupProps) {
  return (
    <ContextMenu.RadioGroup value={value} onValueChange={onValueChange}>
      {children}
    </ContextMenu.RadioGroup>
  );
}

export const BaseContextMenuRadioItem = forwardRef<
  ComponentRef<typeof ContextMenu.RadioItem>,
  BaseContextMenuRadioItemProps
>(function BaseContextMenuRadioItem(
  {
    children,
    label,
    icon,
    shortcut,
    tone,
    disabled = false,
    inset = false,
    value,
    appearance,
    className,
    classNames,
    style,
    onSelect,
    ...itemProps
  },
  ref,
) {
  validateBoolean(disabled, 'RadioItem disabled');
  validateBoolean(inset, 'RadioItem inset');
  validateTone(tone);
  const resolvedTone = tone ?? 'default';
  const resolvedAppearance = useMonkeysBaseAppearance(appearance);
  const componentAttributes = useMonkeysComponentAttributes();
  const theme = resolveBaseAppearance(resolvedAppearance);

  return (
    <ContextMenu.RadioItem
      {...componentAttributes}
      {...itemProps}
      ref={ref}
      value={value}
      disabled={disabled}
      data-monkeys-component="context-menu"
      data-slot="radio-item"
      data-tone={resolvedTone}
      data-value={value}
      data-disabled={disabled || undefined}
      className={cn(
        theme.slots.menuItem,
        'pl-8',
        getBaseMenuItemToneClassName(theme, tone),
        disabled && theme.slots.menuItemDisabled,
        classNames?.root,
        className,
      )}
      style={{
        ...theme.styles.menuItem,
        ...style,
      }}
      onSelect={(event) => {
        if (disabled) {
          event.preventDefault();
          return;
        }
        onSelect?.(event);
      }}
    >
      {renderItemContent({
        children,
        label,
        icon,
        shortcut,
        inset,
        classNames: {
          icon: cn(theme.slots.menuItemIcon, classNames?.icon),
          label: cn(theme.slots.menuItemLabel, classNames?.label),
          shortcut: cn(theme.slots.menuItemShortcut, classNames?.shortcut),
        },
        indicator: (
          <ContextMenu.ItemIndicator
            data-slot="indicator"
            className={cn(theme.slots.menuRadioIndicator, classNames?.indicator)}
          >
            <DotIcon />
          </ContextMenu.ItemIndicator>
        ),
      })}
    </ContextMenu.RadioItem>
  );
});

export function BaseContextMenuSub({
  children,
  open,
  defaultOpen,
  onOpenChange,
}: BaseContextMenuSubProps) {
  if (open !== undefined) validateBoolean(open, 'Sub open');
  if (defaultOpen !== undefined) {
    validateBoolean(defaultOpen, 'Sub defaultOpen');
  }
  return (
    <ContextMenu.Sub
      open={open}
      defaultOpen={defaultOpen}
      onOpenChange={onOpenChange}
    >
      {children}
    </ContextMenu.Sub>
  );
}

export const BaseContextMenuSubTrigger = forwardRef<
  ComponentRef<typeof ContextMenu.SubTrigger>,
  BaseContextMenuSubTriggerProps
>(function BaseContextMenuSubTrigger(
  {
    children,
    label,
    icon,
    tone,
    disabled = false,
    inset = false,
    appearance,
    className,
    classNames,
    style,
    ...itemProps
  },
  ref,
) {
  validateBoolean(disabled, 'SubTrigger disabled');
  validateBoolean(inset, 'SubTrigger inset');
  validateTone(tone);
  const resolvedTone = tone ?? 'default';
  const resolvedAppearance = useMonkeysBaseAppearance(appearance);
  const componentAttributes = useMonkeysComponentAttributes();
  const theme = resolveBaseAppearance(resolvedAppearance);
  const resolvedLabel = children ?? label;

  return (
    <ContextMenu.SubTrigger
      {...componentAttributes}
      {...itemProps}
      ref={ref}
      disabled={disabled}
      data-monkeys-component="context-menu"
      data-slot="sub-trigger"
      data-tone={resolvedTone}
      data-disabled={disabled || undefined}
      className={cn(
        theme.slots.menuSubTrigger,
        getBaseMenuItemToneClassName(theme, tone),
        disabled && theme.slots.menuItemDisabled,
        classNames?.root,
        className,
      )}
      style={{
        ...theme.styles.menuItem,
        ...style,
      }}
    >
      {hasRenderableNode(icon) ? (
        <span
          data-slot="icon"
          className={cn(theme.slots.menuItemIcon, classNames?.icon)}
        >
          {icon}
        </span>
      ) : inset ? (
        <span
          aria-hidden="true"
          data-slot="icon"
          className="h-4 w-4 shrink-0"
        />
      ) : null}
      {hasRenderableNode(resolvedLabel) ? (
        <span
          data-slot="label"
          className={cn(theme.slots.menuItemLabel, classNames?.label)}
        >
          {resolvedLabel}
        </span>
      ) : null}
      <span
        data-slot="sub-indicator"
        className={cn(
          theme.slots.menuSubTriggerIcon,
          classNames?.shortcut,
          'rtl:rotate-180',
        )}
      >
        <ChevronRightIcon />
      </span>
    </ContextMenu.SubTrigger>
  );
});

export const BaseContextMenuSubContent = forwardRef<
  ComponentRef<typeof ContextMenu.SubContent>,
  BaseContextMenuSubContentProps
>(function BaseContextMenuSubContent(
  {
    children,
    appearance,
    className,
    classNames,
    style,
    sideOffset = 6,
    ...subContentProps
  },
  ref,
) {
  const resolvedAppearance = useMonkeysBaseAppearance(appearance);
  const componentAttributes = useMonkeysComponentAttributes();
  const theme = resolveBaseAppearance(resolvedAppearance);
  const portal = useBaseOverlayPortal();
  const content = (
    <ContextMenu.SubContent
      {...componentAttributes}
      {...subContentProps}
      ref={ref}
      sideOffset={sideOffset}
      data-monkeys-component="context-menu"
      data-slot="sub-content"
      className={cn(theme.slots.menuSubContent, classNames?.root, className)}
      style={{
        ...theme.styles.menuContent,
        ...style,
      }}
    >
      {children}
    </ContextMenu.SubContent>
  );

  return portal.shouldPortal ? (
    <ContextMenu.Portal {...portal.radixPortalProps}>
      {content}
    </ContextMenu.Portal>
  ) : content;
});
